import { createBrowserRouter, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { useAuthicatedUser } from "react-redux-django-auth";
import NoPage from "../components/pages/NoPage";
import { PageLoader } from "../utils/loader";
import StudentDashboardLayout from "../features/students/layout";
import StudentDashboard from "../features/students/dashboard";
import StudentPayments from "../features/students/payments";
import Courses from "../features/students/courses";
import StudentCourseDetail from "../features/students/detail pages/studentCourseDetail";
import StudentTrackDetail from "../features/students/detail pages/studentTrackDetail";
import MyCourses from "../features/students/myCourses";
import CommunityHub from "../features/students/community";
import Resources from "../features/students/resources";
import Certification from "../features/students/certification";
import SupportPage from "../features/students/support";
import Profile from "../features/dashboard/Profile/profile";
import Events from "../features/students/events/event";
import Notifications from "../features/students/notifications";

// Instructor Dashboard Imports
import InstructorDashboardLayout from "../features/instructors/layout";
import InstructorDashboard from "../features/instructors/dashboard";
import InstructorCourses from "../features/instructors/courses";
import InstructorStudents from "../features/instructors/students";
import InstructorAssignments from "../features/instructors/assignments";
import InstructorMessages from "../features/instructors/messages";
import InstructorSettings from "../features/instructors/settings";
import InstructorCreateCourse from "../features/instructors/createCourse";
import InstructorCourseDetail from "../features/instructors/courseDetail";
import InstructorCourseEditor from "../features/instructors/courseEditor";

// Admin Dashboard Imports
import AdminDashboardLayout from "../features/admin/layout";
import AdminDashboard from "../features/admin/dashboard";
import AdminUsers from "../features/admin/users";
import AdminCourses from "../features/admin/courses";
import AdminTracks from "../features/admin/tracks";
import AdminReports from "../features/admin/reports";
import AdminStaffJobs from "../features/admin/staffJobs";
import AdminVisitors from "../features/admin/visitors";
import AdminBlog from "../features/admin/blog";
import AdminCreateBlog from "../features/admin/createBlog";
import AdminPayments from "../features/admin/payments";
import AdminMonthlyLedger from "../features/admin/MonthlyLedger";
import AdminPriceList from "../features/admin/priceList";
import AdminSocialBroadcast from "../features/admin/socialBroadcast";

// SuperAdmin Dashboard Imports
import SuperAdminDashboardLayout from "../features/superadmin/layout";
import SuperAdminDashboard from "../features/superadmin/dashboard";
import SuperAdminUsers from "../features/superadmin/users";
import SuperAdminCourses from "../features/superadmin/courses";
import SuperAdminTracks from "../features/superadmin/tracks";
import SuperAdminReports from "../features/superadmin/reports";
import SuperAdminStaffJobs from "../features/superadmin/staffJobs";
import SuperAdminVisitors from "../features/superadmin/visitors";
import SuperAdminBlog from "../features/superadmin/blog";
import SuperAdminCreateBlog from "../features/superadmin/createBlog";
import SuperAdminEditBlog from "../features/superadmin/editBlog";
import SuperAdminRoles from "../features/superadmin/roles";
import SuperAdminPayments from "../features/superadmin/payments";
import SuperAdminMediaLibrary from "../features/superadmin/mediaLibrary";
import SuperAdminSocialBroadcast from "../features/superadmin/socialBroadcast";
import SuperAdminEnrollments from "../features/superadmin/enrollments";
import SuperAdminMonthlyLedger from "../features/superadmin/MonthlyLedger";
import SuperAdminPriceList from "../features/superadmin/priceList";
import SuperAdminCompanySettings from "../features/superadmin/companySettings";

// Verification Dashboard Imports
import VerificationDashboard from "../features/verification/dashboard";
import ServicesPage from "../features/verification/services";
import LogsPage from "../features/verification/logs";
import WalletPage from "../features/verification/wallet";

import Layout from "../components/pages/layout";
import Home from "../components/pages/Home";
import About from "../components/pages/About";
import Services from "../components/pages/Services";
// import ServicesDetail from "../components/detailedPages/servicesDetail";
import RecentJob from "../components/pages/RecentJobs";
// import RecentJobDetail from "../components/detailedPages/recentJobDetail";
// import Course from "../components/pages/Course";
// import CourseDetail from "../components/detailedPages/courseDetail";
// import TrackDetail from "../components/detailedPages/trackDetail";
import Privacy from "../components/pages/PrivacyPolicy";
import OurReturnPolicy from "../components/pages/ReturnPolicy";
import OurTerms from "../components/pages/TermsAndConditions";
import Blog from "../components/pages/blog/blog";
// import BlogDetail from "../components/pages/blog/blogDetail";
import Contact from "../components/pages/Contact";
import Careers from "../components/pages/Careers";
import JobOpeningDetail from "../components/detailedPages/jobOpeningDetail";
import LogIn from "../features/auth/LogIn";
import SignUp from "../features/auth/SignUp";
import ResetPassword from "../features/auth/RestPassword";
import NewPasswordConfirm from "../features/auth/NewPasswordSetup";
import ActivateAccount from "../features/auth/Activate";
import AuthGuard from "../features/auth/AuthGuard";
import { ROLES } from "../constants/roles";
import DashboardRedirect from "../features/auth/DashboardRedirect";
import CourseLearning from "../features/students/detail pages/courseLearning";
import GenerateBirthdayFlyer from "../components/pages/birthdayFlyer";
import VerifyCertificate from "../components/pages/VerifyCertificate";

const BlogDetail = lazy(() =>
  import("../components/pages/blog/blogDetail").then((m: any) => ({
    default: m.default || m.BlogDetail || m,
  }))
);
const ServicesDetail = lazy(() =>
  import("../components/detailedPages/servicesDetail").then((m: any) => ({
    default: m.default || m.ServicesDetail || m,
  }))
);

const RecentJobDetail = lazy(() =>
  import("../components/detailedPages/recentJobDetail").then((m: any) => ({
    default: m.default || m.RecentJobDetail || m,
  }))
);
const Course = lazy(() =>
  import("../components/pages/Course").then((m: any) => ({
    default: m.default || m.Course || m,
  }))
);
const CourseDetail = lazy(() =>
  import("../components/detailedPages/courseDetail").then((m: any) => ({
    default: m.default || m.CourseDetail || m,
  }))
);
const TrackDetail = lazy(() =>
  import("../components/detailedPages/trackDetail").then((m: any) => ({
    default: m.default || m.TrackDetail || m,
  }))
);

const VerificationRedirect: React.FC<{ subpath?: string }> = ({ subpath = "" }) => {
  const { currentUser } = useAuthicatedUser();
  const userData = (currentUser as any)?.user || (currentUser as any)?.data?.user || currentUser;
  const isSuperAdmin = Boolean(
    userData?.is_superuser ||
    userData?.role === "SUPERADMIN" ||
    (Array.isArray(userData?.roles) && userData.roles.includes("SUPERADMIN"))
  );
  const target = isSuperAdmin
    ? `/superadmin/dashboard/verification${subpath ? `/${subpath}` : ""}`
    : `/staff/dashboard/verification${subpath ? `/${subpath}` : ""}`;
  return <Navigate to={target} replace />;
};

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <NoPage />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: "about",
        element: <About />,
      },
      {
        path: "services",
        element: <Services />,
      },
      {
        path: "services/:slug",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ServicesDetail />
          </Suspense>
        ),
      },
      {
        path: "recent-jobs",
        element: <RecentJob />,
      },
      {
        path: "recent-jobs/:slug",
        element: (
          <Suspense fallback={<PageLoader />}>
            <RecentJobDetail />
          </Suspense>
        ),
      },
      {
        path: "contact",
        element: <Contact />,
      },
      {
        path: "privacy-policy",
        element: <Privacy />,
      },
      {
        path: "return-policy",
        element: <OurReturnPolicy />,
      },
      {
        path: "terms-and-conditions",
        element: <OurTerms />,
      },
      {
        path: "careers",
        element: <Careers />,
      },
      {
        path: "careers/:slug",
        element: <JobOpeningDetail />,
      },
      {
        path: "track",
        element: (
          <Suspense fallback={<PageLoader />}>
            <Course />
          </Suspense>
        ),
      },
      {
        path: "courses",
        element: (
          <Suspense>
            <Course />
          </Suspense>
        ),
      },
      {
        path: "courses/:slug",
        element: (
          <Suspense fallback={<PageLoader />}>
            <CourseDetail />
          </Suspense>
        ),
      },
      {
        path: "track/:slug",
        element: (
          <Suspense fallback={<PageLoader />}>
            <TrackDetail />
          </Suspense>
        ),
      },
      {
        path: "blog",
        element: <Blog />,
      },
      {
        path: "blog/:slug",
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogDetail />
          </Suspense>
        ),
      },
      {
        path: "generate-birthday-flyer",
        element: (
          <Suspense fallback={<PageLoader />}>
            <GenerateBirthdayFlyer />
          </Suspense>
        ),
      },
      {
        path: "verify-certificate",
        element: <VerifyCertificate />,
      },
    ],
  },
  {
    path: "/",
    errorElement: <NoPage />,
    children: [
      {
        path: "login",
        element: (
          <AuthGuard>
            <LogIn />
          </AuthGuard>
        ),
      },
      {
        path: "create-account",
        element: (
          <AuthGuard>
            <SignUp />
          </AuthGuard>
        ),
      },
      {
        path: "signup",
        element: <Navigate to="/create-account" replace />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "password/reset/confirm/:uid/:token",
        element: <NewPasswordConfirm />,
      },
      {
        path: "activate/:uid/:token",
        element: <ActivateAccount />,
      },
    ],
  },
  {
    path: "/dashboard",
    element: <DashboardRedirect />,
  },
  {
    path: "/dashboard/student",
    element: <Navigate to="/student/dashboard" replace />,
  },
  {
    path: "/dashboard/instructor",
    element: <Navigate to="/instructor/dashboard" replace />,
  },
  {
    path: "/dashboard/admin",
    element: <Navigate to="/staff/dashboard" replace />,
  },
  {
    path: "/dashboard/staff",
    element: <Navigate to="/staff/dashboard" replace />,
  },
  {
    path: "/dashboard/superadmin",
    element: <Navigate to="/superadmin/dashboard" replace />,
  },
  {
    path: "/student/dashboard",
    element: (
      <AuthGuard>
        <StudentDashboardLayout />
      </AuthGuard>
    ),
    errorElement: <NoPage />,
    children: [
      {
        index: true,
        element: <StudentDashboard />,
      },
      {
        path: "payments",
        element: <StudentPayments />,
      },
      {
        path: "courses",
        element: <Courses />,
      },
      {
        path: "courses/:slug",
        element: <StudentCourseDetail />,
      },
      {
        path: "track/:slug",
        element: <StudentCourseDetail />,
        // element: <StudentTrackDetail />,
      },
      {
        path: "my-courses",
        element: <MyCourses />,
      },
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "settings",
        element: <Profile />,
      },
      {
        path: "event",
        element: <Events />,
      },
      {
        path: "notifications",
        element: <Notifications />,
      },
      {
        path: "community",
        element: <CommunityHub />,
      },
      {
        path: "resources",
        element: <Resources />,
      },
      {
        path: "certification",
        element: <Certification />,
      },
      {
        path: "support",
        element: <SupportPage />,
      },
      {
        path: "courses/:slug/enrolled",
        element: <CourseLearning />,
      },
      {
        path: "track/:slug/enrolled",
        element: <CourseLearning />,
      },
    ],
  },
  {
    path: "/instructor/dashboard",
    element: (
      <AuthGuard>
        <InstructorDashboardLayout />
      </AuthGuard>
    ),
    errorElement: <NoPage />,
    children: [
      {
        index: true,
        element: <InstructorDashboard />,
      },
      {
        path: "my-courses",
        element: <InstructorCourses />,
      },
      {
        path: "my-courses/:slug/preview",
        element: <CourseLearning previewMode />,
      },
      {
        path: "my-tracks/:slug/preview",
        element: <CourseLearning previewMode />,
      },
      {
        path: "students",
        element: <InstructorStudents />,
      },
      {
        path: "assignments",
        element: <InstructorAssignments />,
      },
      {
        path: "messages",
        element: <InstructorMessages />,
      },
      {
        path: "event",
        element: <Events />,
      },
      {
        path: "community",
        element: <CommunityHub />,
      },
      {
        path: "resources",
        element: <Resources />,
      },
      {
        path: "support",
        element: <SupportPage />,
      },
      {
        path: "create-course",
        element: <InstructorCreateCourse />,
      },
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "settings",
        element: <InstructorSettings />,
      },
      {
        path: "my-courses/:id",
        element: <InstructorCourseDetail />,
      },
      {
        path: "my-courses/edit/:id",
        element: <InstructorCourseEditor />,
      },
      {
        path: "my-courses/create",
        element: <InstructorCourseEditor />,
      },
      {
        path: "notifications",
        element: <Notifications />,
      },
    ],
  },
  {
    path: "/staff/dashboard",
    element: (
      <AuthGuard>
        <AdminDashboardLayout />
      </AuthGuard>
    ),
    errorElement: <NoPage />,
    children: [
      {
        index: true,
        element: <AdminDashboard />,
      },
      {
        path: "users",
        element: <AdminUsers />,
      },
      {
        path: "courses",
        element: <AdminCourses />,
      },
      {
        path: "courses/:slug/preview",
        element: <CourseLearning previewMode />,
      },
      {
        path: "tracks",
        element: <AdminTracks />,
      },
      {
        path: "tracks/:slug/preview",
        element: <CourseLearning previewMode />,
      },
      {
        path: "reports",
        element: <AdminReports />,
      },
      {
        path: "payments",
        element: <AdminPayments />,
      },
      {
        path: "income-expenditure",
        element: <AdminMonthlyLedger />,
      },
      {
        path: "staff-jobs",
        element: <AdminStaffJobs />,
      },
      {
        path: "visitors",
        element: <AdminVisitors />,
      },
      {
        path: "price-list",
        element: <AdminPriceList />,
      },
      {
        path: "blog",
        element: <AdminBlog />,
      },
      {
        path: "blog/create",
        element: <AdminCreateBlog />,
      },
      {
        path: "social-broadcast",
        element: <AdminSocialBroadcast />,
      },
      {
        path: "verification",
        element: <VerificationDashboard />,
      },
      {
        path: "verification/services",
        element: <ServicesPage />,
      },
      {
        path: "verification/logs",
        element: <LogsPage />,
      },
      {
        path: "verification/wallet",
        element: <WalletPage />,
      },
      {
        path: "notifications",
        element: <Notifications />,
      },
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "settings",
        element: <Profile />,
      },
    ],
  },
  {
    path: "/superadmin/dashboard",
    element: (
      <AuthGuard>
        <SuperAdminDashboardLayout />
      </AuthGuard>
    ),
    errorElement: <NoPage />,
    children: [
      {
        index: true,
        element: <SuperAdminDashboard />,
      },
      {
        path: "users",
        element: <SuperAdminUsers />,
      },
      {
        path: "roles",
        element: <SuperAdminRoles />,
      },
      {
        path: "courses",
        element: <SuperAdminCourses />,
      },
      {
        path: "courses/:slug/preview",
        element: <CourseLearning previewMode />,
      },
      {
        path: "courses/create",
        element: <InstructorCourseEditor />,
      },
      {
        path: "courses/edit/:id",
        element: <InstructorCourseEditor />,
      },
      {
        path: "tracks",
        element: <SuperAdminTracks />,
      },
      {
        path: "tracks/:slug/preview",
        element: <CourseLearning previewMode />,
      },
      {
        path: "tracks/create",
        element: <InstructorCourseEditor />,
      },
      {
        path: "tracks/edit/:id",
        element: <InstructorCourseEditor />,
      },
      {
        path: "enrollments",
        element: <SuperAdminEnrollments />,
      },
      {
        path: "reports",
        element: <SuperAdminReports />,
      },
      {
        path: "payments",
        element: <SuperAdminPayments />,
      },
      {
        path: "income-expenditure",
        element: <SuperAdminMonthlyLedger />,
      },
      {
        path: "staff-jobs",
        element: <SuperAdminStaffJobs />,
      },
      {
        path: "visitors",
        element: <SuperAdminVisitors />,
      },
      {
        path: "price-list",
        element: <SuperAdminPriceList />,
      },
      {
        path: "blog",
        element: <SuperAdminBlog />,
      },
      {
        path: "blog/create",
        element: <SuperAdminCreateBlog />,
      },
      {
        path: "blog/edit/:id",
        element: <SuperAdminEditBlog />,
      },
      {
        path: "media-library",
        element: <SuperAdminMediaLibrary />,
      },
      {
        path: "social-broadcast",
        element: <SuperAdminSocialBroadcast />,
      },
      {
        path: "verification",
        element: <VerificationDashboard />,
      },
      {
        path: "verification/services",
        element: <ServicesPage />,
      },
      {
        path: "verification/logs",
        element: <LogsPage />,
      },
      {
        path: "verification/wallet",
        element: <WalletPage />,
      },
      {
        path: "notifications",
        element: <Notifications />,
      },
      {
        path: "company-settings",
        element: <SuperAdminCompanySettings />,
      },
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "settings",
        element: <Profile />,
      },
    ],
  },
  {
    path: "/verification",
    element: <VerificationRedirect />,
  },
  {
    path: "/verification/dashboard",
    element: <VerificationRedirect />,
  },
  {
    path: "/verification/services",
    element: <VerificationRedirect subpath="services" />,
  },
  {
    path: "/verification/dashboard/services",
    element: <VerificationRedirect subpath="services" />,
  },
  {
    path: "/verification/logs",
    element: <VerificationRedirect subpath="logs" />,
  },
  {
    path: "/verification/dashboard/logs",
    element: <VerificationRedirect subpath="logs" />,
  },
  {
    path: "/verification/wallet",
    element: <VerificationRedirect subpath="wallet" />,
  },
  {
    path: "/verification/dashboard/wallet",
    element: <VerificationRedirect subpath="wallet" />,
  },
]);
